/**
 * sqlite3mt durable local persistence contract
 *
 * Unified interface from §96 §6.1–§6.5.
 *
 * This module consolidates all durable storage contracts that the
 * Control Worker, bootstrap, commit-registration, push, reconcile,
 * and snapshot-persistence subsystems depend on.
 *
 * The actual file-based implementation lives separately; this file
 * defines only the normative contract that any implementation must
 * satisfy, plus the snapshot/journal/intent types from §6.2–§6.4.
 *
 * Why this is the hardest next slice:
 * - Every component depends on it (commit registration, bootstrap,
 *   reconcile, push finalisation, snapshot persistence)
 * - Crash recovery semantics are the most correctness-critical part
 *   of the entire architecture
 * - §6.5 durability ordering constraints must be preserved
 * - Still bounded: 4 distinct stores with clear interfaces
 * - Verifiable: each store has testable invariants
 */

import type {
    Delta,
    FrameMax,
    PageHash,
    Pgno,
    PublicHead,
    ReconcileIntentPhase,
    Root,
    WalSnapshot,
} from './types'

// ── §6.2 Journal entry ─────────────────────────────────────────

/**
 * A single entry in the append-only local commit journal.
 *
 * §6.2: The journal contains only local user commits.
 * Reconcile imports are handled via reconcile-intent + snapshot.
 */
export interface DurableLocalCommitJournalEntry {
    commitSeq: bigint
    workspaceBaseRoot: Root
    workspaceBaseVersion: bigint
    workspaceBasePageCount: number
    walSalt0: number
    walSalt1: number
    frameMax: FrameMax
    delta: Delta
}

// ── §6.3 Snapshot ──────────────────────────────────────────────

/**
 * A complete point-in-time snapshot of Control Worker state.
 *
 * §6.3: Must be written with atomic replace.
 *
 * The snapshot captures all state needed to reconstruct runtime
 * without replaying the entire journal. After a snapshot is
 * written, journal entries up through snapshot.localCommitSeq
 * can be truncated.
 */
export interface DurableLocalSnapshot {
    schemaVersion: 3

    dbBaseRoot: Root
    dbOverlay: Map<Pgno, PageHash>
    dbPageCount: number

    workspaceBaseHead: PublicHead
    workspaceOverlay: Map<Pgno, PageHash>
    workspacePageCount: number

    pendingDbDeltas: Map<FrameMax, Delta>
    currentWalSalt0: number | null
    currentWalSalt1: number | null
    integratedFrameMax: number
    lastWalSnapshot: WalSnapshot | null

    localCommitSeq: bigint

    requiredBlobHashes: Set<PageHash>
    requiredManifestNodeHashes: Set<string>
}

// ── §6.4 Reconcile intent ──────────────────────────────────────

/**
 * A durable reconcile intent record.
 *
 * §6.4 semantics:
 * - PREPARED: plan is durably described but no physical import started
 * - APPENDING: exact append attempt durably reserved; outcome unknown
 *   until runtime marks COMMITTED or bootstrap verifies/excludes interval
 * - COMMITTED: import durably known as successful; must apply if
 *   snapshot does not yet reflect it
 *
 * This type is re-exported from bootstrap.ts for convenience;
 * the canonical definition lives there because bootstrap is the
 * primary consumer.
 */
export type { DurableReconcileIntent } from './bootstrap'

// ── §6.4 Reconcile intent attempt metadata ─────────────────────

/** Metadata written when an intent transitions PREPARED → APPENDING. */
export interface ReconcileAppendAttempt {
    attemptStartFrame: number
    attemptEndFrame: number
    attemptWalSalt0: number
    attemptWalSalt1: number
}

/** Metadata written when an intent transitions APPENDING → COMMITTED. */
export interface ReconcileCommitMeta {
    commitFrameMax: number | null
    commitPostWalSalt0: number | null
    commitPostWalSalt1: number | null
}

// ── §6.1 Blob store transaction ────────────────────────────────

/**
 * Atomic transaction context for blob + journal writes.
 *
 * §6.5: Blobs needed for replay of unpushed commits must be
 * durable before journal commit. The transaction enforces this
 * by bundling blob puts and journal appends into a single
 * atomic unit.
 */
export interface DurableStoreTransaction {
    /** Idempotent CAS put of a page blob. */
    putBlob(hash: PageHash, bytes: Uint8Array): void | Promise<void>

    /** Append a local commit entry to the journal. */
    appendLocalCommit(entry: DurableLocalCommitJournalEntry): void | Promise<void>
}

// ── §6 DurableLocalStore — unified contract ─────────────────────

/**
 * The complete durable local persistence contract from §6.
 *
 * This interface is what every Control Worker runtime depends on.
 * Any implementation (file-based, in-memory for tests, etc.) must
 * satisfy the §6.5 durability ordering:
 *
 *  1. Local commits must be durably journaled before ACK
 *  2. Blobs/manifest nodes needed for journal replay must be
 *     durable before journal commit
 *  3. Snapshot may be written coalesced after journal commit
 *  4. Reconcile intent must be durable PREPARED before remote import
 *  5. Each blob in importBlobsByHash must be durable before intent write
 *  6. PREPARED → APPENDING must be durable before WAL frame append
 *  7. APPENDING → COMMITTED must be durable before base-advance fence release
 *  8. Reconcile intent may only be cleared after snapshot reflecting new base
 *     is durably written
 */
export interface DurableLocalStore {
    // ── §6.1 Blob store ──────────────────────────────────────

    /** Idempotent CAS put of a page blob. */
    putBlob(hash: PageHash, bytes: Uint8Array): Promise<void>

    /** Get a page blob by content hash. Returns null if not found. */
    getBlob(hash: PageHash): Promise<Uint8Array | null>

    /** Idempotent put of a manifest node blob. */
    putManifestNode(hash: string, bytes: Uint8Array): Promise<void>

    /** Get a manifest node blob by hash. Returns null if not found. */
    getManifestNode(hash: string): Promise<Uint8Array | null>

    // ── §6.2 Journal ─────────────────────────────────────────

    /**
     * Read journal entries after the given commitSeq.
     *
     * Returns entries in ascending commitSeq order.
     * Pass -1n to read all entries.
     */
    readJournalAfter(afterSeq: bigint): Promise<DurableLocalCommitJournalEntry[]>

    /**
     * Truncate journal entries up to and including the given commitSeq.
     *
     * Only safe to call after a snapshot reflecting this seq has been
     * durably written (§6.5 rule 3).
     */
    truncateJournalThrough(throughSeq: bigint): Promise<void>

    // ── §6.2 Atomic blob+journal transaction ─────────────────

    /**
     * Execute an atomic transaction that bundles blob puts and journal
     * appends. The transaction must provide the §6.5 guarantee that
     * all putBlob calls are durable before appendLocalCommit commits.
     */
    transaction<T>(
        callback: (tx: DurableStoreTransaction) => Promise<T> | T
    ): Promise<T>

    // ── §6.3 Snapshot ────────────────────────────────────────

    /**
     * Load the most recent durable snapshot.
     *
     * Returns null if no snapshot exists (fresh database or first boot).
     */
    loadSnapshot(): Promise<DurableLocalSnapshot | null>

    /**
     * Write a complete snapshot with atomic replace semantics.
     *
     * §6.3: Must be written with atomic replace so that a crash
     * during write never leaves a corrupt/partial snapshot.
     */
    writeSnapshot(snapshot: DurableLocalSnapshot): Promise<void>

    // ── §6.4 Reconcile intent ────────────────────────────────

    /**
     * Load the current durable reconcile intent, if any.
     *
     * Returns null if no intent exists.
     */
    loadReconcileIntentOrNull(): Promise<import('./bootstrap').DurableReconcileIntent | null>

    /**
     * Write or overwrite the durable reconcile intent.
     *
     * §6.4: Must be durable before any physical import attempt.
     * The intent includes phase, WAL metadata, import delta,
     * and residual overlay.
     */
    writeReconcileIntent(
        intent: import('./bootstrap').DurableReconcileIntent
    ): Promise<void>

    /**
     * Mark a reconcile intent as APPENDING with the attempt metadata.
     *
     * §6.5 rule 6: Must be durable before WAL frame append.
     */
    markReconcileIntentAppending(
        intentId: bigint,
        attempt: ReconcileAppendAttempt
    ): Promise<void>

    /**
     * Mark a reconcile intent as COMMITTED with the commit metadata.
     *
     * §6.5 rule 7: Must be durable before base-advance fence release.
     */
    markReconcileIntentCommitted(
        intentId: bigint,
        committed: ReconcileCommitMeta
    ): Promise<void>

    /**
     * Clear the durable reconcile intent.
     *
     * §6.5 rule 8: Only safe after snapshot reflecting new base
     * is durably written.
     */
    clearReconcileIntent(intentId: bigint): Promise<void>

    /**
     * Clear all local durable state for this runtime identity.
     *
     * This is only valid at a bootstrap generation boundary before CONTROL_READY
     * is published. It is used when the authoritative remote lane has been
     * reset/rewound behind the locally persisted generation, so the previous
     * snapshot, journal, reconcile intent, and CAS caches no longer share a
     * valid lineage with the server head.
     */
    clearAllLocalState(): Promise<void>
}

// ── §6 In-memory implementation (for tests and reference) ────────

/**
 * In-memory implementation of DurableLocalStore.
 *
 * This is the reference implementation used for testing and as a
 * foundation for future file-backed stores. All §6.5 durability
 * ordering constraints are enforced as in-memory sequencing.
 *
 * The store is NOT safe for concurrent access from multiple agents;
 * it provides logical atomicity within transactions only.
 */
export class InMemoryDurableLocalStore implements DurableLocalStore {
    // §6.1 Blob stores
    private _blobs = new Map<PageHash, Uint8Array>()
    private _manifestNodes = new Map<string, Uint8Array>()

    // §6.2 Journal
    private _journal: DurableLocalCommitJournalEntry[] = []

    // §6.3 Snapshot
    private _snapshot: DurableLocalSnapshot | null = null

    // §6.4 Reconcile intent
    private _reconcileIntent: import('./bootstrap').DurableReconcileIntent | null = null

    // ── §6.1 Blob store ──────────────────────────────────────

    async putBlob(hash: PageHash, bytes: Uint8Array): Promise<void> {
        if (!this._blobs.has(hash)) {
            this._blobs.set(hash, new Uint8Array(bytes))
        }
    }

    async getBlob(hash: PageHash): Promise<Uint8Array | null> {
        const found = this._blobs.get(hash)
        return found ? new Uint8Array(found) : null
    }

    async putManifestNode(hash: string, bytes: Uint8Array): Promise<void> {
        if (!this._manifestNodes.has(hash)) {
            this._manifestNodes.set(hash, new Uint8Array(bytes))
        }
    }

    async getManifestNode(hash: string): Promise<Uint8Array | null> {
        const found = this._manifestNodes.get(hash)
        return found ? new Uint8Array(found) : null
    }

    // ── §6.2 Journal ─────────────────────────────────────────

    async readJournalAfter(afterSeq: bigint): Promise<DurableLocalCommitJournalEntry[]> {
        const result: DurableLocalCommitJournalEntry[] = []
        for (const entry of this._journal) {
            if (entry.commitSeq > afterSeq) {
                result.push(cloneJournalEntry(entry))
            }
        }
        return result
    }

    async truncateJournalThrough(throughSeq: bigint): Promise<void> {
        this._journal = this._journal.filter(e => e.commitSeq > throughSeq)
    }

    // ── §6.2 Atomic blob+journal transaction ─────────────────

    async transaction<T>(
        callback: (tx: DurableStoreTransaction) => Promise<T> | T
    ): Promise<T> {
        const pendingBlobs = new Map<PageHash, Uint8Array>()
        const pendingCommits: DurableLocalCommitJournalEntry[] = []

        const tx: DurableStoreTransaction = {
            putBlob(hash: PageHash, bytes: Uint8Array): void | Promise<void> {
                if (!pendingBlobs.has(hash)) {
                    pendingBlobs.set(hash, new Uint8Array(bytes))
                }
            },
            appendLocalCommit(entry: DurableLocalCommitJournalEntry): void | Promise<void> {
                pendingCommits.push(cloneJournalEntry(entry))
            },
        }

        const result = await callback(tx)

        assertJournalAppendOrder(this._journal, pendingCommits)

        // §6.5 rules 1–2: blobs must be durable before journal commit
        for (const [hash, bytes] of pendingBlobs) {
            if (!this._blobs.has(hash)) {
                this._blobs.set(hash, bytes)
            }
        }

        // Now commit journal entries — they see the blobs
        for (const entry of pendingCommits) {
            this._journal.push(entry)
        }

        return result
    }

    // ── §6.3 Snapshot ────────────────────────────────────────

    async loadSnapshot(): Promise<DurableLocalSnapshot | null> {
        if (this._snapshot == null) return null
        return cloneSnapshot(this._snapshot)
    }

    async writeSnapshot(snapshot: DurableLocalSnapshot): Promise<void> {
        this._snapshot = cloneSnapshot(snapshot)
    }

    // ── §6.4 Reconcile intent ────────────────────────────────

    async loadReconcileIntentOrNull(): Promise<import('./bootstrap').DurableReconcileIntent | null> {
        if (this._reconcileIntent == null) return null
        return cloneReconcileIntent(this._reconcileIntent)
    }

    async writeReconcileIntent(
        intent: import('./bootstrap').DurableReconcileIntent
    ): Promise<void> {
        this._reconcileIntent = cloneReconcileIntent(intent)
    }

    async markReconcileIntentAppending(
        intentId: bigint,
        attempt: ReconcileAppendAttempt
    ): Promise<void> {
        if (this._reconcileIntent == null) {
            throw new Error(`no reconcile intent to mark APPENDING (intentId=${intentId})`)
        }
        if (this._reconcileIntent.intentId !== intentId) {
            throw new Error(`intentId mismatch: expected ${this._reconcileIntent.intentId}, got ${intentId}`)
        }
        if (this._reconcileIntent.phase !== 'PREPARED') {
            throw new Error(`can only mark APPENDING from PREPARED, got ${this._reconcileIntent.phase}`)
        }
        this._reconcileIntent.phase = 'APPENDING'
        this._reconcileIntent.attemptStartFrame = attempt.attemptStartFrame
        this._reconcileIntent.attemptEndFrame = attempt.attemptEndFrame
        this._reconcileIntent.attemptWalSalt0 = attempt.attemptWalSalt0
        this._reconcileIntent.attemptWalSalt1 = attempt.attemptWalSalt1
    }

    async markReconcileIntentCommitted(
        intentId: bigint,
        committed: ReconcileCommitMeta
    ): Promise<void> {
        if (this._reconcileIntent == null) {
            throw new Error(`no reconcile intent to mark COMMITTED (intentId=${intentId})`)
        }
        if (this._reconcileIntent.intentId !== intentId) {
            throw new Error(`intentId mismatch: expected ${this._reconcileIntent.intentId}, got ${intentId}`)
        }
        if (this._reconcileIntent.phase !== 'APPENDING') {
            throw new Error(`can only mark COMMITTED from APPENDING, got ${this._reconcileIntent.phase}`)
        }
        this._reconcileIntent.phase = 'COMMITTED'
        this._reconcileIntent.commitFrameMax = committed.commitFrameMax
        this._reconcileIntent.commitPostWalSalt0 = committed.commitPostWalSalt0
        this._reconcileIntent.commitPostWalSalt1 = committed.commitPostWalSalt1
    }

    async clearReconcileIntent(intentId: bigint): Promise<void> {
        if (this._reconcileIntent == null) return
        if (this._reconcileIntent.intentId !== intentId) {
            throw new Error(`intentId mismatch: expected ${this._reconcileIntent.intentId}, got ${intentId}`)
        }
        this._reconcileIntent = null
    }

    async clearAllLocalState(): Promise<void> {
        this._blobs.clear()
        this._manifestNodes.clear()
        this._journal = []
        this._snapshot = null
        this._reconcileIntent = null
    }

    // ── Test helpers (not part of DurableLocalStore contract) ──

    /** Return number of journal entries (for test assertions). */
    get journalSize(): number {
        return this._journal.length
    }

    /** Return whether a snapshot exists (for test assertions). */
    get hasSnapshot(): boolean {
        return this._snapshot != null
    }

    /** Return whether a reconcile intent exists (for test assertions). */
    get hasReconcileIntent(): boolean {
        return this._reconcileIntent != null
    }

    /** Return current reconcile intent phase (for test assertions). */
    get reconcileIntentPhase(): import('./types').ReconcileIntentPhase | null {
        return this._reconcileIntent?.phase ?? null
    }

    /** Return number of stored page blobs (for test assertions). */
    get blobCount(): number {
        return this._blobs.size
    }

    /** Return number of stored manifest nodes (for test assertions). */
    get manifestNodeCount(): number {
        return this._manifestNodes.size
    }
}

// ── Clone helpers ────────────────────────────────────────────────

function cloneJournalEntry(e: DurableLocalCommitJournalEntry): DurableLocalCommitJournalEntry {
    return {
        commitSeq: e.commitSeq,
        workspaceBaseRoot: cloneRoot(e.workspaceBaseRoot),
        workspaceBaseVersion: e.workspaceBaseVersion,
        workspaceBasePageCount: e.workspaceBasePageCount,
        walSalt0: e.walSalt0,
        walSalt1: e.walSalt1,
        frameMax: e.frameMax,
        delta: cloneDelta(e.delta),
    }
}

function cloneDelta(d: Delta): Delta {
    return {
        pageHashes: new Map(d.pageHashes),
        postPageCount: d.postPageCount,
    }
}

function cloneRoot(root: Root): Root {
    return {
        rootId: root.rootId,
        meta: {
            manifestRootId: root.meta.manifestRootId,
            pageCount: root.meta.pageCount,
            parentPublicRootId: root.meta.parentPublicRootId,
        },
    }
}

function clonePublicHead(head: PublicHead): PublicHead {
    return {
        root: cloneRoot(head.root),
        version: head.version,
    }
}

function cloneSnapshot(s: DurableLocalSnapshot): DurableLocalSnapshot {
    return {
        schemaVersion: s.schemaVersion,
        dbBaseRoot: cloneRoot(s.dbBaseRoot),
        dbOverlay: new Map(s.dbOverlay),
        dbPageCount: s.dbPageCount,
        workspaceBaseHead: clonePublicHead(s.workspaceBaseHead),
        workspaceOverlay: new Map(s.workspaceOverlay),
        workspacePageCount: s.workspacePageCount,
        pendingDbDeltas: new Map(
            Array.from(s.pendingDbDeltas, ([k, v]) => [k, cloneDelta(v)])
        ),
        currentWalSalt0: s.currentWalSalt0,
        currentWalSalt1: s.currentWalSalt1,
        integratedFrameMax: s.integratedFrameMax,
        lastWalSnapshot: s.lastWalSnapshot == null ? null : { ...s.lastWalSnapshot },
        localCommitSeq: s.localCommitSeq,
        requiredBlobHashes: new Set(s.requiredBlobHashes),
        requiredManifestNodeHashes: new Set(s.requiredManifestNodeHashes),
    }
}

function cloneReconcileIntent(
    i: import('./bootstrap').DurableReconcileIntent
): import('./bootstrap').DurableReconcileIntent {
    return {
        intentId: i.intentId,
        phase: i.phase,
        baseRoot: cloneRoot(i.baseRoot),
        baseVersion: i.baseVersion,
        basePageCount: i.basePageCount,
        remoteRoot: cloneRoot(i.remoteRoot),
        remoteVersion: i.remoteVersion,
        walSalt0: i.walSalt0,
        walSalt1: i.walSalt1,
        caughtUpFrameMax: i.caughtUpFrameMax,
        importDelta: cloneDelta(i.importDelta),
        residualOverlay: new Map(i.residualOverlay),
        residualPageCount: i.residualPageCount,
        attemptStartFrame: i.attemptStartFrame,
        attemptEndFrame: i.attemptEndFrame,
        attemptWalSalt0: i.attemptWalSalt0,
        attemptWalSalt1: i.attemptWalSalt1,
        commitFrameMax: i.commitFrameMax,
        commitPostWalSalt0: i.commitPostWalSalt0,
        commitPostWalSalt1: i.commitPostWalSalt1,
    }
}

function assertJournalAppendOrder(
    committed: DurableLocalCommitJournalEntry[],
    pending: DurableLocalCommitJournalEntry[]
): void {
    let previousSeq = committed.length > 0
        ? committed[committed.length - 1].commitSeq
        : null
    for (const entry of pending) {
        if (previousSeq != null && entry.commitSeq <= previousSeq) {
            throw new Error('local commit journal must append in strictly increasing commitSeq order')
        }
        previousSeq = entry.commitSeq
    }
}
